import { MutatorCallback, mutate } from 'swr'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { NOTIFICATION_API_URL } from 'modules/profile/notifications/api/notification-center'
import { NotificationsApiInterface } from 'modules/profile/notifications/types/notification-types'

interface MutateAllNotificationInterface {
  mutator?: MutatorCallback<NotificationsApiInterface>
  matcher?: (key: string) => boolean
  shouldRevalidate?: boolean
}

export interface GetNotificationLinkResponse {
  link: string
}

export const useNotificationsActions = () => {
  const { fetcher: postFetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.post>({
    method: RequestMethodsEnum.post,
  })

  const { fetcher: linkFetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    GetNotificationLinkResponse
  >({
    method: RequestMethodsEnum.get,
  })

  const markAllNotificationsAsRead = async () => {
    await postFetcher(`${NOTIFICATION_API_URL}/notifications/mark-all-as-read`)
    await globalReadNotificationUpdate()
  }

  const markNotificationAsRead = async (id: string) => {
    await postFetcher(`${NOTIFICATION_API_URL}/notifications/${id}/mark-as-read`)
    await globalReadNotificationUpdate(id)
  }

  const getNotificationLink = async (id: string) => {
    const data = await linkFetcher(`${NOTIFICATION_API_URL}/notifications/${id}`)
    await globalReadNotificationUpdate(id)

    return data
  }

  const mutateAllNotifications = ({
    mutator,
    matcher,
    shouldRevalidate = false,
  }: MutateAllNotificationInterface) =>
    mutate<NotificationsApiInterface>(
      key =>
        typeof key === 'string' &&
        key.startsWith(`${NOTIFICATION_API_URL}/notifications`) &&
        (matcher ? matcher(key) : true),
      mutator,
      shouldRevalidate,
    )

  const globalReadNotificationUpdate = async (id?: string) => {
    if (id) {
      await mutateAllNotifications({
        matcher: key => !key.includes('filter[onlyUnRead]=true'),
        mutator: data => {
          if (data && data.items) {
            return {
              items: data.items.map(item => (item.id === id ? { ...item, read: true } : item)),
              hasMore: data.hasMore,
            }
          }
        },
      })
    } else {
      await mutateAllNotifications({
        matcher: key => !key.includes('filter[onlyUnRead]=true'),
        mutator: data => {
          if (data && data.items) {
            return {
              items: data.items.map(item => ({ ...item, read: true })),
              hasMore: data.hasMore,
            }
          }
        },
      })
    }

    // fetch updated data for lists consisting of unreads
    await mutateAllNotifications({
      matcher: key => key.includes('filter[onlyUnRead]=true'),
      shouldRevalidate: true,
    })

    // update global hasUnread flag from API
    await mutate(`${NOTIFICATION_API_URL}/notifications/has-unread`)
  }

  return {
    markAllNotificationsAsRead,
    markNotificationAsRead,
    getNotificationLink,
    mutateAllNotifications,
    globalReadNotificationUpdate,
  }
}
